import {
  AlertBlockType,
  AlertBlockVariant,
  ButtonType,
  CollapsibleSection,
  Disclaimer,
  DisclaimerVariant,
  FormButton,
  FormButtonTheme,
  FullScreenAlertVariant,
  IconName,
  RadioButton,
  RadioButtonTheme,
  UseNotifications,
  useNotifications,
  UseState,
} from '@elearning-platform/ui';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { Form, Formik, FormikProps } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { TransProps, useTranslation } from 'react-i18next';

import { finishFormAttempt, getFormStructure, saveFormAnswer } from '../../../../api/requests';
import { QueryKey, RouteNameEnum, ViewImage } from '../../../../enums';
import { useAuth, useRedirectTo } from '../../../../hooks';
import { UseAuth, UseRedirectTo } from '../../../../interfaces';

import { KrkVerificationFormField, KrkVerificationFormStep } from './krkVerificationForm.enums';
import { krkVerificationFormSchema } from './krkVerificationForm.schema';
import {
  ButtonWrapper,
  CollapsibleSectionInner,
  ColumnImg,
  Container,
  FieldLabel,
  FieldsWrapper,
  FormColumn,
  FormStep,
  FormWrapper,
  InfoBox,
  MobileImg,
  RadioInputs,
  StyledAlertBlock,
  StyledDisclaimer,
  Title,
} from './krkVerificationForm.styled';
import { KrkVerificationFormProps, KrkVerificationFormValues, StatementsSection, VerificationFormField } from './krkVerificationForm.types';

export const KrkVerificationForm: React.FC<KrkVerificationFormProps> = (props: KrkVerificationFormProps): JSX.Element => {
  const { formId }: KrkVerificationFormProps = props;
  const { t }: TransProps<never> = useTranslation();
  const { addToast, showFullscreenAlert, hideFullscreenAlert }: UseNotifications = useNotifications();
  const { redirect }: UseRedirectTo = useRedirectTo();
  const [step, setStep]: UseState<KrkVerificationFormStep> = useState<KrkVerificationFormStep>(KrkVerificationFormStep.Init);
  const [activeStatement, setActiveStatement]: UseState<number> = useState<number>(0);
  const [isRequestInProgress, setIsRequestInProgress]: UseState<boolean> = useState<boolean>(false);
  const { forceUserInfoCacheReload }: UseAuth = useAuth();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: formStructureResponse }: UseQueryResult<any> = useQuery({
    queryKey: [QueryKey.KrkForm, formId],
    enabled: !!formId,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    queryFn: (): Promise<any> => {
      if (!formId) {
        return Promise.reject(new Error('queryFn: formId is required'));
      }

      return getFormStructure(formId);
    },
  });

  useEffect(
    (): void => {
      if (step === KrkVerificationFormStep.Init) {
        showFullscreenAlert({
          title: t('toyota.app.actionRequiredToAccess.krkVerificationForm.init.title'),
          description: t('toyota.app.actionRequiredToAccess.krkVerificationForm.init.description'),
          variant: FullScreenAlertVariant.Dark,
          acceptButtonData: {
            label: t('toyota.app.actionRequiredToAccess.krkVerificationForm.init.acceptButtonLabel'),
            onClick: (): void => {
              setStep(KrkVerificationFormStep.Form);
              hideFullscreenAlert();
            },
          },
        });
      }
      if (step === KrkVerificationFormStep.Finish) {
        showFullscreenAlert({
          title: t('toyota.app.actionRequiredToAccess.krkVerificationForm.finish.title'),
          description: t('toyota.app.actionRequiredToAccess.krkVerificationForm.finish.description'),
          variant: FullScreenAlertVariant.Dark,
          acceptButtonData: {
            label: t('toyota.app.actionRequiredToAccess.krkVerificationForm.finish.acceptButtonLabel'),
            onClick: async (): Promise<void> => {
              await forceUserInfoCacheReload();
              redirect({ route: RouteNameEnum.Dashboard });
              hideFullscreenAlert();
            },
          },
        });
      }
    },
    [step, hideFullscreenAlert, showFullscreenAlert, redirect, t, forceUserInfoCacheReload]
  );

  const submitAction: (sectionId: string, values: KrkVerificationFormValues, isLastSection: boolean) => void = (
    sectionId: string, values: KrkVerificationFormValues, isLastSection: boolean
  ): void => {
    if (!formId) {
      return;
    }

    setIsRequestInProgress(true);
    saveFormAnswer(
      formId,
      {
        section: sectionId,
        data: { fields: values },
      }
    ).then((): void => {
      if (isLastSection) {
        setIsRequestInProgress(true);
        finishFormAttempt(formId)
          .then((): void => {
            setStep(KrkVerificationFormStep.Finish);
          })
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .catch((error: any): void => addToast({ text: error.response.data.message }))
          .finally((): void => setIsRequestInProgress(false));
      } else {
        setActiveStatement((prev: number): number => prev + 1);
      }
    })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .catch((error: any): void => addToast({ text: error.response.data.message }))
      .finally((): void => setIsRequestInProgress(false));
  };

  const statementsData: StatementsSection[] = useMemo(
    (): StatementsSection[] => {
      if (!formStructureResponse || !Object.keys(formStructureResponse.data.sections).length) {
        return [];
      }

      return Object.keys(formStructureResponse.data.sections).map((key: string, index: number): StatementsSection => ({
        sectionId: key,
        label: t('toyota.app.actionRequiredToAccess.krkVerificationForm.statementLabel', { id: index + 1 }),
        disclaimer: t('toyota.app.actionRequiredToAccess.krkVerificationForm.commonDisclaimer'),
        info: t(`toyota.app.actionRequiredToAccess.krkVerificationForm.statement${index + 1}Info`),
        warning: t('toyota.app.actionRequiredToAccess.krkVerificationForm.commonWarning'),
      }));
    },
    [formStructureResponse, t]
  );

  const fields: VerificationFormField[] = useMemo(
    (): VerificationFormField[] => [
      {
        name: KrkVerificationFormField.FullLegalCapacity,
        question: t('toyota.app.actionRequiredToAccess.krkVerificationForm.fullLegalCapacity.question'),
      },
      {
        name: KrkVerificationFormField.NotConvicted,
        question: t('toyota.app.actionRequiredToAccess.krkVerificationForm.notConvicted.question'),
        info: t('toyota.app.actionRequiredToAccess.krkVerificationForm.notConvicted.info'),
      },
      {
        name: KrkVerificationFormField.ProperAgencyPerformance,
        question: t('toyota.app.actionRequiredToAccess.krkVerificationForm.properAgencyPerformance.question'),
      },
    ],
    [t]
  );

  return (
    <Container>
      {step === KrkVerificationFormStep.Form && (
        <FormStep>
          <FormColumn>
            <FormWrapper>
              <MobileImg src={ViewImage.ActionRequiredToAccessPlatformKrkColumnImgMobile} />
              <Title text={t('toyota.app.actionRequiredToAccess.krkVerificationForm.pageTitle')} />
              {statementsData.map((statement: StatementsSection, index: number): JSX.Element => (
                <Formik<KrkVerificationFormValues>
                  initialValues={{
                    full_legal_capacity: undefined,
                    not_convicted: undefined,
                    proper_agency_performance: undefined,
                  }}
                  key={statement.sectionId}
                  onSubmit={(values: KrkVerificationFormValues): void => {
                    submitAction(statement.sectionId, values, index === statementsData.length - 1);
                  }}
                  validateOnMount
                  validationSchema={krkVerificationFormSchema}
                >
                  {({ values, setFieldValue, isValid }: FormikProps<KrkVerificationFormValues>): JSX.Element => (
                    <Form>
                      <CollapsibleSection
                        isCompleted={activeStatement > index}
                        isOpen
                        title={statement.label}
                      >
                        <CollapsibleSectionInner>
                          <Disclaimer
                            icon={IconName.KeyframeLinear}
                            title={t('toyota.app.actionRequiredToAccess.krkVerificationForm.warning')}
                            variant={DisclaimerVariant.Brand}
                          >
                            {statement.disclaimer}
                          </Disclaimer>
                          <InfoBox text={statement.info} />
                          <StyledDisclaimer
                            icon={IconName.Flare}
                            title={t('toyota.app.actionRequiredToAccess.krkVerificationForm.warning')}
                            variant={DisclaimerVariant.Brand}
                          >
                            {statement.warning}
                          </StyledDisclaimer>
                          {fields.map((item: VerificationFormField): JSX.Element => (
                            <FieldsWrapper key={item.name}>
                              {!!item.info && (
                                <StyledAlertBlock
                                  description={item.info}
                                  label={t('toyota.app.global.info')}
                                  type={AlertBlockType.Brand}
                                  variant={AlertBlockVariant.Vertical}
                                />
                              )}
                              <FieldLabel text={item.question} />
                              <RadioInputs>
                                <RadioButton
                                  checked={values[item.name] === '1'}
                                  label={t('competence.app.global.yes')}
                                  onChange={(): void => {
                                    void setFieldValue(item.name, '1');
                                  }}
                                  radioButtonTheme={RadioButtonTheme.WithBox}
                                />
                                <RadioButton
                                  checked={values[item.name] === '0'}
                                  label={t('competence.app.global.no')}
                                  onChange={(): void => {
                                    void setFieldValue(item.name, '0');
                                  }}
                                  radioButtonTheme={RadioButtonTheme.WithBox}
                                />
                              </RadioInputs>
                            </FieldsWrapper>
                          ))}
                          <ButtonWrapper>
                            <FormButton
                              disabled={!isValid}
                              icon={IconName.Saved}
                              isLoading={isRequestInProgress}
                              label={t('competence.app.global.save')}
                              reversed
                              type={ButtonType.Submit}
                              variant={FormButtonTheme.PrimaryGrey}
                            />
                          </ButtonWrapper>
                        </CollapsibleSectionInner>
                      </CollapsibleSection>
                    </Form>
                  )}
                </Formik>
              ))}
            </FormWrapper>
          </FormColumn>
          <ColumnImg src={ViewImage.ActionRequiredToAccessPlatformKrkColumnImg} />
        </FormStep>
      )}
    </Container>
  );
};
